import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { useMediaGallery, type UseMediaGalleryResult } from '@/hooks/useMediaGallery.hook';
import { moderationApi } from '@/services/moderation.service';
import { rentalsApi } from '@/services/rentals.service';
import type {
  AdminReportListItem,
  AdminShouttDetail,
  AdminShouttListItem,
  AdminShouttTab,
  AiFlaggedItem,
  ModerationNoteForm,
  ModerationSelection,
  ModerationTab,
  ReportStatus,
  ShouttModerationAction,
} from '@/types/moderation.types';
import type { AdminListingDetail } from '@/types/rental.types';
import { rejectShouttSchema, resolveReportSchema } from '@/validators/moderation.validator';

import { PAGE_SIZE, SHOUTT_TABS } from '../constants';

export const MODERATION_QUERY_KEY = 'moderation';

interface UseModerationPageResult {
  // tab + shared state
  tab: ModerationTab;
  isShouttTab: boolean;
  search: string;
  campusId: string;
  reportStatus: ReportStatus | '';
  campuses: { id: string; name: string }[];
  page: number;
  // data per tab
  shoutts: AdminShouttListItem[];
  reports: AdminReportListItem[];
  aiFlagged: AiFlaggedItem[];
  total: number;
  totalPages: number;
  queueCount: number;
  reportedCount: number;
  openReportsCount: number;
  aiFlaggedCount: number;
  // ui state
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  mutatingId: string | null;
  // detail drawer
  selected: ModerationSelection | null;
  shouttDetail: AdminShouttDetail | undefined;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  // AI Flagged listing drawer
  aiListingItem: AiFlaggedItem | null;
  aiListingDetail: AdminListingDetail | undefined;
  aiListingGallery: UseMediaGalleryResult;
  isLoadingAiListing: boolean;
  isErrorAiListing: boolean;
  // inline note form (reject reason / resolve note)
  noteFormFor: ModerationNoteForm | null;
  noteDraft: string;
  noteError: string | null;
  // handlers
  onChangeTab: (tab: ModerationTab) => void;
  onChangeSearch: (value: string) => void;
  onChangeCampus: (campusId: string) => void;
  onChangeReportStatus: (status: ReportStatus | '') => void;
  onChangePage: (page: number) => void;
  onViewShoutt: (item: AdminShouttListItem) => void;
  onViewAiFlagged: (item: AiFlaggedItem) => void;
  onViewReport: (item: AdminReportListItem) => void;
  onCloseDetail: () => void;
  onApprove: (item: AdminShouttListItem) => void;
  onRemove: (item: AdminShouttListItem) => void;
  onRestore: (item: AdminShouttListItem) => void;
  onOpenReject: (item: AdminShouttListItem) => void;
  onOpenResolve: (item: AdminReportListItem) => void;
  onDismissAiFlag: (item: AiFlaggedItem) => void;
  onRemoveAiFlagged: (item: AiFlaggedItem) => void;
  isRescanning: boolean;
  onRescan: () => void;
  onChangeNote: (value: string) => void;
  onCancelNote: () => void;
  onSubmitNote: () => void;
}

export const useModerationPage = (): UseModerationPageResult => {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<ModerationTab>('all');
  const [search, setSearch] = useState('');
  const [campusId, setCampusId] = useState('');
  const [reportStatus, setReportStatus] = useState<ReportStatus | ''>('');
  const [page, setPage] = useState(1);

  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<ModerationSelection | null>(null);

  const [noteFormFor, setNoteFormFor] = useState<ModerationNoteForm | null>(null);
  const [noteTargetId, setNoteTargetId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const isShouttTab = SHOUTT_TABS.includes(tab);

  const campusOptionsQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'campuses'],
    queryFn: () => rentalsApi.listCampusOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const shouttsQuery = useQuery({
    queryKey: [
      MODERATION_QUERY_KEY,
      'shoutts',
      { tab, search: debouncedSearch, campusId, page },
    ],
    queryFn: () =>
      moderationApi.listShoutts({
        tab: tab as AdminShouttTab,
        search: debouncedSearch || undefined,
        campusId: campusId || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: isShouttTab,
    placeholderData: (previous) => previous,
  });

  const reportsQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'reports', { reportStatus, page }],
    queryFn: () =>
      moderationApi.listReports({
        status: reportStatus || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: tab === 'reports',
    placeholderData: (previous) => previous,
  });

  const aiFlaggedQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'ai-flagged', { page }],
    queryFn: () => moderationApi.listAiFlagged({ page, limit: PAGE_SIZE }),
    enabled: tab === 'ai_flagged',
    placeholderData: (previous) => previous,
  });

  // Lightweight counts so the tab badges and metric cards show before those tabs open.
  const aiFlaggedCountQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'ai-flagged-count'],
    queryFn: () => moderationApi.listAiFlagged({ page: 1, limit: 1 }),
    staleTime: 60 * 1000,
  });

  const openReportsCountQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'open-reports-count'],
    queryFn: () => moderationApi.listReports({ status: 'open', page: 1, limit: 1 }),
    staleTime: 60 * 1000,
  });

  const reportedCountQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'reported-count'],
    queryFn: () => moderationApi.listShoutts({ tab: 'reported', page: 1, limit: 1 }),
    staleTime: 60 * 1000,
  });

  const queueCountQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'queue-count'],
    queryFn: () => moderationApi.listShoutts({ tab: 'all', page: 1, limit: 1 }),
    staleTime: 60 * 1000,
  });

  const detailQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'shoutt', selected?.item.id],
    queryFn: () => moderationApi.getShoutt(selected!.item.id),
    enabled: selected?.kind === 'shoutt' || selected?.kind === 'aiShoutt',
    staleTime: 30_000,
    retry: 1,
  });

  // A flagged listing is fetched from the listings endpoint — the AI queue row carries an id and
  // a verdict, nothing an admin can act on.
  const aiListingItem = selected?.kind === 'aiListing' ? selected.item : null;

  const aiListingQuery = useQuery({
    queryKey: [MODERATION_QUERY_KEY, 'listing', aiListingItem?.id],
    queryFn: () => rentalsApi.getListing(aiListingItem!.id),
    enabled: !!aiListingItem,
    staleTime: 30_000,
    retry: 1,
  });

  const aiListingGallery = useMediaGallery(
    aiListingQuery.data?.photos ?? [],
    aiListingQuery.data?.video,
    aiListingItem?.id,
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [MODERATION_QUERY_KEY] });
  };

  const closeNoteForm = (): void => {
    setNoteFormFor(null);
    setNoteTargetId(null);
    setNoteDraft('');
    setNoteError(null);
  };

  const shouttMutation = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: ShouttModerationAction; reason?: string }) => {
      switch (action) {
        case 'approve':
          return moderationApi.approveShoutt(id);
        case 'reject':
          return moderationApi.rejectShoutt(id, reason);
        case 'remove':
          return moderationApi.removeShoutt(id);
        case 'restore':
          return moderationApi.restoreShoutt(id);
      }
    },
    onMutate: ({ id }) => setMutatingId(id),
    onSuccess: () => closeNoteForm(),
    onError: (error) => setNoteError(error instanceof Error ? error.message : 'Action failed'),
    onSettled: () => {
      setMutatingId(null);
      invalidate();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      moderationApi.resolveReport(id, note),
    onMutate: ({ id }) => setMutatingId(id),
    onSuccess: (updated) => {
      closeNoteForm();
      setSelected((current) =>
        current?.kind === 'report' && current.item.id === updated.id
          ? { kind: 'report', item: updated }
          : current,
      );
    },
    onError: (error) => setNoteError(error instanceof Error ? error.message : 'Action failed'),
    onSettled: () => {
      setMutatingId(null);
      invalidate();
    },
  });

  const aiFlagMutation = useMutation({
    mutationFn: ({ action, item }: { action: 'dismiss' | 'remove'; item: AiFlaggedItem }) =>
      action === 'dismiss'
        ? moderationApi.dismissAiFlag(item.kind, item.id)
        : moderationApi.removeAiFlagged(item.kind, item.id),
    onMutate: ({ item }) => setMutatingId(item.id),
    onSuccess: () => setSelected(null),
    onSettled: () => {
      setMutatingId(null);
      invalidate();
    },
  });

  const rescanMutation = useMutation({
    mutationFn: () => moderationApi.rescan(),
    onSettled: () => invalidate(),
  });

  const resetAndSet =
    <T,>(setter: (value: T) => void) =>
    (value: T): void => {
      setter(value);
      setPage(1);
    };

  const openNoteForm = (form: ModerationNoteForm, targetId: string): void => {
    setNoteFormFor(form);
    setNoteTargetId(targetId);
    setNoteDraft('');
    setNoteError(null);
  };

  const submitNote = (): void => {
    if (!noteFormFor || !noteTargetId) return;

    if (noteFormFor === 'reject') {
      const parsed = rejectShouttSchema.safeParse({ reason: noteDraft });
      if (!parsed.success) {
        setNoteError(parsed.error.errors[0]?.message ?? 'Invalid reason');
        return;
      }
      shouttMutation.mutate({ id: noteTargetId, action: 'reject', reason: parsed.data.reason });
      return;
    }

    const parsed = resolveReportSchema.safeParse({ note: noteDraft || undefined });
    if (!parsed.success) {
      setNoteError(parsed.error.errors[0]?.message ?? 'Invalid note');
      return;
    }
    resolveMutation.mutate({ id: noteTargetId, note: parsed.data.note });
  };

  const activeQuery =
    tab === 'reports' ? reportsQuery : tab === 'ai_flagged' ? aiFlaggedQuery : shouttsQuery;

  return {
    tab,
    isShouttTab,
    search,
    campusId,
    reportStatus,
    campuses: campusOptionsQuery.data ?? [],
    page: activeQuery.data?.page ?? page,
    shoutts: shouttsQuery.data?.items ?? [],
    reports: reportsQuery.data?.items ?? [],
    aiFlagged: aiFlaggedQuery.data?.items ?? [],
    total: activeQuery.data?.total ?? 0,
    totalPages: activeQuery.data?.totalPages ?? 1,
    queueCount: queueCountQuery.data?.total ?? 0,
    reportedCount: reportedCountQuery.data?.total ?? 0,
    openReportsCount: openReportsCountQuery.data?.total ?? 0,
    aiFlaggedCount: aiFlaggedCountQuery.data?.total ?? 0,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    errorMessage: activeQuery.error instanceof Error ? activeQuery.error.message : null,
    mutatingId,
    selected,
    shouttDetail: detailQuery.data,
    isLoadingDetail: detailQuery.isLoading && detailQuery.fetchStatus !== 'idle',
    isErrorDetail: detailQuery.isError,
    aiListingItem,
    aiListingDetail: aiListingQuery.data,
    aiListingGallery,
    isLoadingAiListing: aiListingQuery.isLoading && aiListingQuery.fetchStatus !== 'idle',
    isErrorAiListing: aiListingQuery.isError,
    noteFormFor,
    noteDraft,
    noteError,
    onChangeTab: (next) => {
      setTab(next);
      setPage(1);
      setSelected(null);
      closeNoteForm();
    },
    onChangeSearch: resetAndSet(setSearch),
    onChangeCampus: resetAndSet(setCampusId),
    onChangeReportStatus: resetAndSet(setReportStatus),
    onChangePage: setPage,
    onViewShoutt: (item) => {
      setSelected({ kind: 'shoutt', item });
      closeNoteForm();
    },
    onViewAiFlagged: (item) => {
      setSelected({ kind: item.kind === 'listing' ? 'aiListing' : 'aiShoutt', item });
      closeNoteForm();
    },
    onViewReport: (item) => {
      setSelected({ kind: 'report', item });
      closeNoteForm();
    },
    onCloseDetail: () => {
      setSelected(null);
      closeNoteForm();
    },
    onApprove: (item) => shouttMutation.mutate({ id: item.id, action: 'approve' }),
    onRemove: (item) => shouttMutation.mutate({ id: item.id, action: 'remove' }),
    onRestore: (item) => shouttMutation.mutate({ id: item.id, action: 'restore' }),
    onOpenReject: (item) => {
      setSelected({ kind: 'shoutt', item });
      openNoteForm('reject', item.id);
    },
    onOpenResolve: (item) => {
      setSelected({ kind: 'report', item });
      openNoteForm('resolve', item.id);
    },
    onDismissAiFlag: (item) => aiFlagMutation.mutate({ action: 'dismiss', item }),
    onRemoveAiFlagged: (item) => aiFlagMutation.mutate({ action: 'remove', item }),
    isRescanning: rescanMutation.isPending,
    onRescan: () => {
      rescanMutation.mutate(undefined, {
        onSuccess: (result) => {
          setTab('ai_flagged');
          setPage(1);
          // Scans run in the background — refresh the AI Flagged tab a few times as
          // results land (each item is a Claude call, so they trickle in over ~a minute).
          [5, 15, 30, 60].forEach((s) => setTimeout(invalidate, s * 1000));
          // eslint-disable-next-line no-alert
          window.alert(
            `Re-scan started for ${result.queued} item${result.queued === 1 ? '' : 's'}. ` +
              'Flagged items will appear in the AI Flagged tab over the next minute.',
          );
        },
        onError: (error) =>
          // eslint-disable-next-line no-alert
          window.alert(error instanceof Error ? error.message : 'Re-scan failed'),
      });
    },
    onChangeNote: (value) => {
      setNoteDraft(value);
      setNoteError(null);
    },
    onCancelNote: closeNoteForm,
    onSubmitNote: submitNote,
  };
};
